/**
 * Pure deterministic simulation types — an into-the-screen runner.
 * Axes: +X right (lateral steer), +Y up (jump/crouch), +Z forward (auto-run,
 * "into the screen"). No Babylon/DOM imports, no wall-clock; `dt` is passed in.
 */
import type { LevelDef } from '../level/types.js';

export type { LevelDef };

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export type Phase = 'run' | 'atGate' | 'beamActive' | 'complete';
export type HandleState = 'idle' | 'operating' | 'latched';

export interface PlayerState {
  /** Center of the player box: x lateral, y vertical (center), z forward. */
  pos: Vec3;
  /** Vertical velocity (forward speed is constant; lateral is direct). */
  vy: number;
  grounded: boolean;
  crouching: boolean;
  coyoteTimer: number;
  jumpBuffer: number;
  /** Forward distance accumulator → footstep cadence. */
  strideAccum: number;
}

export interface SimState {
  t: number;
  phase: Phase;
  player: PlayerState;
  boardsRead: boolean[];
  nearBoard: number;
  nearObstacle: number;
  nearHandle: boolean;
  inBeam: boolean;
  handleHigh: HandleState;
  handleCorner: HandleState;
  beamActive: boolean;
  complete: boolean;
}

export interface SimInput {
  /** Lateral steer, -1..1 (left/right). */
  steer: number;
  jump: boolean; // edge
  crouch: boolean; // held
  interact: boolean; // edge
}

export interface SimConfig {
  gravity: number;
  jumpVel: number;
  forwardSpeed: number;
  lateralSpeed: number;
  trackHalfWidth: number;
  coyoteTime: number;
  jumpBufferTime: number;
  playerW: number; // x extent
  playerH: number; // y extent (standing)
  crouchH: number; // y extent (crouched)
  playerD: number; // z extent
  strideLength: number;
  boardReadRadius: number;
  handleReachRadius: number;
}

export type SimEvent =
  | { type: 'footstep' }
  | { type: 'jump' }
  | { type: 'land' }
  | { type: 'bump'; obstacle: number } // ran into something (non-lethal warn)
  | { type: 'board-enter'; index: number }
  | { type: 'board-exit'; index: number }
  | { type: 'handle-reject' }
  | { type: 'handle-operate' }
  | { type: 'beam-on' }
  | { type: 'complete' };

export const DEFAULT_SIM_CONFIG: SimConfig = {
  gravity: 38,
  jumpVel: 13,
  forwardSpeed: 9,
  lateralSpeed: 7,
  trackHalfWidth: 5,
  coyoteTime: 0.1,
  jumpBufferTime: 0.12,
  playerW: 0.9,
  playerH: 1.6,
  crouchH: 0.8,
  playerD: 0.9,
  strideLength: 1.3,
  boardReadRadius: 3.2,
  handleReachRadius: 1.8,
};
